import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import OrderPrintSheet, { OrderPrintCss } from "@/components/orders/OrderPrintSheet";
import { Printer, ArrowLeft, Warning } from "@phosphor-icons/react";

/**
 * Página de impressão em lote — abre em nova aba via /orders/print-batch?ids=id1,id2,...
 * Renderiza cada pedido como uma OP separada, uma por página A4.
 * Auto-dispara window.print() quando todos os pedidos carregarem.
 */
export default function BatchPrint() {
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const hasToken = !!getUser();

  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;
    async function load() {
      if (ids.length === 0) { setLoading(false); return; }
      const results = await Promise.all(ids.map((id) =>
        api.get(`/orders/${id}`)
          .then(({ data }) => ({ ok: true, order: data }))
          .catch(() => ({ ok: false, id }))
      ));
      if (cancelled) return;
      setOrders(results.filter((r) => r.ok).map((r) => r.order));
      setErrors(results.filter((r) => !r.ok).map((r) => r.id));
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [ids, hasToken]);

  // Auto-print: aguarda todos os pedidos E todas as imagens (artes) carregarem
  useEffect(() => {
    if (loading || orders.length === 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const imgs = Array.from(document.querySelectorAll("[data-testid='batch-preview'] img"));
      await Promise.all(imgs.map((img) =>
        img.complete ? Promise.resolve() : new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
          setTimeout(res, 8000);
        })
      ));
      if (!cancelled) window.print();
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [loading, orders.length]);

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  const buildSummary = (order) => {
    // Reconstroi productionSummary (por tipo) e totalCups a partir dos items
    const byType = {};
    let total = 0;
    for (const it of order.order_items || []) {
      const type = it.type || "Genérico";
      if (!byType[type]) byType[type] = { type, total_cups: 0, by_color: {} };
      byType[type].total_cups += it.cups || 0;
      total += it.cups || 0;
      for (const cb of it.color_breakdown || []) {
        byType[type].by_color[cb.color] = (byType[type].by_color[cb.color] || 0) + (cb.qty || 0);
      }
    }
    return { productionSummary: Object.values(byType), totalCups: total };
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <OrderPrintCss />

      {/* Toolbar — visível só na tela */}
      <div className="bg-black text-white p-4 flex items-center justify-between print:hidden" data-testid="batch-toolbar">
        <div className="flex items-center gap-3">
          <Printer size={22} weight="bold" />
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Impressão em Lote</div>
            <div className="font-bold text-lg">
              {loading ? "Carregando..." : `${orders.length} ${orders.length === 1 ? "ordem" : "ordens"} de produção`}
              {errors.length > 0 && <span className="text-[#FFC000] text-xs ml-3">({errors.length} não encontrada{errors.length !== 1 ? "s" : ""})</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            disabled={loading || orders.length === 0}
            className="px-4 py-2 bg-white text-black font-bold text-sm uppercase tracking-widest hover:bg-yellow-100 disabled:opacity-40 flex items-center gap-2"
            data-testid="batch-print-btn"
          >
            <Printer size={16} weight="bold" /> Imprimir Todos
          </button>
          <button onClick={() => window.close()} className="px-4 py-2 border border-white text-white font-bold text-xs uppercase tracking-widest hover:bg-white/10" data-testid="batch-close-btn">
            <ArrowLeft size={14} weight="bold" className="inline mr-1" /> Fechar
          </button>
        </div>
      </div>

      {loading && (
        <div className="p-16 text-center text-zinc-500 print:hidden">
          <div className="animate-pulse font-mono">Buscando {ids.length} pedidos...</div>
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div className="p-16 text-center print:hidden" data-testid="batch-empty">
          <Warning size={40} weight="thin" className="mx-auto text-[#E60000] mb-3" />
          <div className="text-lg font-bold">Nenhum pedido encontrado</div>
          <div className="text-sm text-zinc-500 mt-1">Verifique os IDs passados na URL.</div>
        </div>
      )}

      {/* Preview antes de imprimir — 1 OP por "página" simulada */}
      <div className="max-w-[210mm] mx-auto py-6 print:py-0 print:max-w-none" data-testid="batch-preview">
        {orders.map((order, idx) => {
          const { productionSummary, totalCups } = buildSummary(order);
          const isLast = idx === orders.length - 1;
          return (
            <div
              key={order.id}
              className="bg-white shadow-lg p-6 mb-6 print:shadow-none print:p-0 print:mb-0 batch-op-page"
              style={{
                // Força quebra de página entre OPs (menos a última) — funciona no @media print
                pageBreakAfter: isLast ? "auto" : "always",
                breakAfter: isLast ? "auto" : "page",
              }}
              data-testid={`batch-op-${order.bling_number || idx}`}
            >
              <OrderPrintSheet
                order={order}
                notes={order.notes || null}
                imageUrl={order.image_url || null}
                productionSummary={productionSummary}
                totalCups={totalCups}
                sheetId={`op-sheet-${order.id}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
